import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../components/PageHeader';
import { useData } from '../context/DataContext';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  ArrowRight, 
  Newspaper, 
  Megaphone, 
  Mic2, 
  Grid, 
  Search
} from 'lucide-react';
import Translate from '../components/Translate';

const NewsMedia = () => {
  const { data, loading } = useData();
  const news = data.news || { latest: [], upcoming: [], press: [] };
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'latest' | 'upcoming' | 'press'
  const [searchQuery, setSearchQuery] = useState('');

  if (loading) {
    return (
      <div className="pt-32 min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-sugarcane-green border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Merge all categories into a single roster
  const allNews = [
    ...(news.latest || []).map(item => ({ ...item, section: 'latest', sectionLabel: 'Latest Updates', icon: Megaphone })),
    ...(news.upcoming || []).map(item => ({ ...item, section: 'upcoming', sectionLabel: 'Upcoming Events', icon: Calendar })),
    ...(news.press || []).map(item => ({ ...item, section: 'press', sectionLabel: 'Press Releases', icon: Mic2 }))
  ];

  // Sort by date or logic (if date matches format "Month Year" or custom, let's keep array ordering)
  const sortedNews = [...allNews];

  // Filter based on active tab selection and search query
  const filteredNews = sortedNews.filter(item => {
    const matchesTab = activeTab === 'all' || item.section === activeTab;
    const matchesSearch = 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      (item.category || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const tabOptions = [
    { id: 'all', label: 'All Insights', icon: Grid },
    { id: 'latest', label: 'Updates', icon: Megaphone },
    { id: 'upcoming', label: 'Events', icon: Calendar },
    { id: 'press', label: 'Press Releases', icon: Mic2 }
  ];

  return (
    <div className="min-h-screen bg-gray-50/50 font-poppins pb-24">
      <PageHeader 
        title={<Translate>Newsroom</Translate>} 
        subtitle={<Translate>Latest News & Corporate Updates</Translate>} 
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Modern Filter & Search Bar */}
        <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4 mb-12">
          {/* Tab Selector Switcher */}
          <div className="flex flex-wrap gap-1.5 w-full md:w-auto">
            {tabOptions.map((tab) => {
              const IconComponent = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setSearchQuery(''); // Reset search on tab switch
                  }}
                  className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 border ${
                    isActive 
                      ? 'bg-sugarcane-green border-sugarcane-green text-white shadow-md shadow-sugarcane-green/10' 
                      : 'bg-white border-transparent text-gray-500 hover:text-sugarcane-charcoal hover:bg-gray-50'
                  }`}
                >
                  <IconComponent size={14} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Search Box Input */}
          <div className="relative w-full md:w-80">
            <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
              <Search size={16} />
            </span>
            <input 
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search news & milestones..."
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-gray-50/50 border border-gray-200 rounded-xl focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none font-medium text-sugarcane-charcoal"
            />
          </div>
        </div>

        {/* Story Roster */}
        {filteredNews.length === 0 ? (
          <div className="py-24 text-center bg-white rounded-2xl border border-gray-100 max-w-md mx-auto shadow-sm">
            <Newspaper size={44} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-sugarcane-charcoal mb-1">No Matches Found</h3>
            <p className="text-gray-400 text-xs px-8 leading-relaxed">
              We couldn't find any articles matching your search criteria. Try a different keyword or reset active filters.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredNews.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg hover:border-sugarcane-green/20 transition-all duration-300 flex flex-col group hover:-translate-y-1.5"
              >
                <div className="aspect-[16/10] overflow-hidden bg-gray-50 relative">
                  <img 
                    src={item.image || 'https://via.placeholder.com/600x400'} 
                    alt={item.title} 
                    className="w-full h-full object-cover transition-transform duration-[1000ms] group-hover:scale-105"
                  />
                </div>
                <div className="p-6 flex-grow flex flex-col justify-between">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="px-2.5 py-0.5 bg-sugarcane-green/10 text-sugarcane-green text-[9px] font-bold uppercase tracking-wider rounded-lg border border-sugarcane-green/20">
                        {item.category || item.sectionLabel}
                      </span>
                      <span className="text-[11px] text-gray-400 font-semibold flex items-center">
                        <Calendar size={12} className="mr-1 text-gray-400" />
                        {item.date}
                      </span>
                    </div>

                    <h4 className="text-md font-bold text-sugarcane-charcoal leading-snug line-clamp-3 group-hover:text-sugarcane-green transition-colors min-h-[4.5rem]">
                      {item.title}
                    </h4>
                  </div>

                  <div className="pt-5 mt-5 border-t border-gray-100 flex justify-end">
                    <Link 
                      to={`/news/${item.id}`}
                      className="inline-flex items-center space-x-1.5 text-sugarcane-green hover:text-sugarcane-charcoal font-bold text-xs transition-colors group/link"
                    >
                      <span>Read Full Story</span>
                      <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default NewsMedia;
